import React, { useEffect, useState, ReactNode } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ChangeNetworkDrawer from "../../Modules/ChangeNetworkDrawer";
import NetworkUnsupportedModal from "../../Modules/NetworkUnsupportedModal";
import PreflightModalTransfer from "../../Modules/PreflightModalTransfer";
import AddressInput from "../Custom/AddressInput";
import TransferActiveModal from "../../Modules/TransferActiveModal";
import { useWeb3 } from "@chainsafe/web3-context";
import {
  ChainbridgeProvider,
  useChainbridge,
} from "../../Contexts/ChainbridgeContext";
import TokenSelectInput from "../Custom/TokenSelectInput";
import TokenInput from "../Custom/TokenInput";

import { ethers, utils } from "ethers";
import { chainbridgeConfig, isPalmNetwork } from "../../chainbridgeConfig";
import Fees from "../Custom/Fees";
import PageHeading from "../UI/PageHeading";
import {
  Heading,
  Text,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Box,
  Stack,
  VStack,
  Flex,
  Button,
  Image,
  useToast,
  Alert,
  Divider,
  Link,
} from "@chakra-ui/react";
import { ExternalLinkIcon, TimeIcon } from "@chakra-ui/icons";
import { toastSuccess, toastError } from "../../config/toast";

import PATCH from "../../media/logos/patch.png";
import CHAINSAFE from "../../media/logos/chainsafe.png";
import CONSENSYS from "../../media/logos/consensys.svg";
import walletService from "../../Services/WalletService";
import CurrentGasFees from "../Custom/CurrentGasFees";

import { getFeatureFlagStatus } from "../../featureFlags";

const shadowForkMessagesEnabled = getFeatureFlagStatus(
  "shadowForkMessagesEnabled"
);

type IntermediateFormValues = {
  tokenAmount: string;
  token: string;
  tokenSymbol: string;
  receiver: string;
};

type PreflightDetails = {
  tokenAmount: number;
  token: string;
  tokenSymbol: string;
  receiver: string;
};

const ethNativeTokenSymbol = "ETH";

const TransferPage = () => {
  const { isReady, checkIsReady, wallet, onboard, tokens, address, network } =
    useWeb3();
  const {
    homeChain,
    destinationChains,
    destinationChain,
    deposit,
    setDestinationChain,
    transactionStatus,
    resetDeposit,
    bridgeFee,
    homeChainIsPalm,
    palmRelayFee,
    palmGreenFee,
    tollTokenSymbol,
    tollTokenDecimals,
    tollTokenAddress,
  } = useChainbridge();
  const toast = useToast();

  const [switchingNetworks, setSwitchingNetworks] = useState<boolean>(false);
  const [walletConnecting, setWalletConnecting] = useState(false);
  const [preflightModalOpen, setPreflightModalOpen] = useState<boolean>(false);
  const [ethereumSettedAsDefault, setEthereumSettedAsDefault] =
    useState<boolean>(false);

  const [intermediateFormValues, setIntermediateFormValues] =
    useState<IntermediateFormValues>({
      receiver: "",
      token: "",
      tokenAmount: "",
      tokenSymbol: "",
    });

  const [preflightDetails, setPreflightDetails] = useState<PreflightDetails>({
    receiver: "",
    token: "",
    tokenAmount: 0,
    tokenSymbol: "",
  });

  // Automatically listen to changes to the intermediate values
  // and update the preflight details if everything checks out
  useEffect(() => {
    let tokenAmountAsNumber = parseFloat(intermediateFormValues.tokenAmount);

    setPreflightDetails({
      ...intermediateFormValues,
      tokenAmount: isNaN(tokenAmountAsNumber) ? 0 : tokenAmountAsNumber,
    });
  }, [intermediateFormValues]);

  useEffect(() => {
    const checkIfIsSupportedNetwork = async () => {
      const chainConnected = await walletService.getChainConnected();
      const chainId = parseInt(chainConnected);
      const supportedNetworks = chainbridgeConfig.chains.map(
        (bc) => bc.networkId
      );
      const isSupported = supportedNetworks.includes(chainId);
      const isInPalm = isPalmNetwork(chainId);

      if (!isSupported || (isInPalm && !ethereumSettedAsDefault)) {
        const ethChain = chainbridgeConfig.chains.find(
          (chain) => chain.nativeTokenSymbol === ethNativeTokenSymbol
        );
        if (ethChain) {
          const { name, networkId } = ethChain;
          handleSwitchNetwork(name, networkId);
          setEthereumSettedAsDefault(true);
        }
      }
    };

    if (isReady) {
      checkIfIsSupportedNetwork();
    }
  }, [isReady]);

  const handleConnect = async () => {
    setWalletConnecting(true);
    !wallet && (await onboard?.walletSelect());
    wallet && (await checkIsReady());
    setWalletConnecting(false);
  };

  const handleSwitchNetwork = async (chainName?: string, chainId?: number) => {
    const id = chainId || destinationChain?.networkId;
    const name = (chainId && chainName) || destinationChain?.name;

    const showToast = (success: boolean) => {
      const title = success
        ? `Chain changed to ${name}`
        : `Sorry, there was an error changing chains`;

      return toast({
        title,
        ...(success ? toastSuccess : toastError),
      });
    };

    if (id) {
      setSwitchingNetworks(true);
      walletService
        .switchNetwork(id)
        .then((result: any) => {
          if (result) {
            return showToast(true);
          } else {
            return showToast(false);
          }
        })
        .catch((error) => {
          return showToast(false);
        })
        .finally(() => setSwitchingNetworks(false));
    }
  };

  const DECIMALS =
    preflightDetails && tokens[preflightDetails.token]
      ? tokens[preflightDetails.token].decimals
      : 18;

  const REGEX =
    DECIMALS > 0
      ? new RegExp(`^[0-9]{1,18}(.[0-9]{1,${DECIMALS}})?$`)
      : new RegExp(`^[0-9]{1,18}?$`);

  const totalFees = () => {
    const relayFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals))
      : bridgeFee;
    const greenFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals))
      : 0;
    return relayFee ? relayFee + greenFee : greenFee;
  };

  const transferSchema = object().shape({
    tokenAmount: string()
      .test("Token selected", "Please select a token", (value) => {
        if (
          !!value &&
          preflightDetails &&
          tokens[preflightDetails.token] &&
          tokens[preflightDetails.token].balance !== undefined
        ) {
          return true;
        } else {
          return false;
        }
      })
      .test("InputValid", "Input invalid", (value) => {
        try {
          return REGEX.test(`${value}`);
        } catch (error) {
          console.error(error);
          return false;
        }
      })
      .test("Max", "Insufficient funds", (value) => {
        if (
          value &&
          preflightDetails &&
          tokens[preflightDetails.token] &&
          tokens[preflightDetails.token].balance
        ) {
          return parseFloat(value) <= tokens[preflightDetails.token].balance;
        }
        return false;
      })
      .test("Min", "Less than minimum", (value) => {
        if (value) {
          return parseFloat(value) > 0;
        }
        return false;
      })
      .test("Bridge fees", "Insufficient funds to pay bridge fees", (value) => {
        const bridgeFees = totalFees();
        const transferTokenAddress = preflightDetails && preflightDetails.token;
        const transferTokenMatchesTollToken =
          tollTokenAddress && transferTokenAddress === tollTokenAddress;
        const transferTokens =
          value && transferTokenMatchesTollToken ? parseFloat(value) : 0;
        const feeToken = tollTokenAddress && tokens[tollTokenAddress];
        if (bridgeFees == 0 || !homeChainIsPalm) {
          return true;
        }

        if (feeToken && feeToken.balance) {
          return bridgeFees + transferTokens <= feeToken.balance;
        }
        return false;
      })
      .required("Please set a value"),
    token: string().required("Please select a token"),
    receiver: string()
      .test("Valid address", "Please add a valid address", (value) => {
        return utils.isAddress(value as string);
      })
      .required("Please add a receiving address"),
  });

  // TODO: line 467: How to pull correct HomeChain Symbol

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<IntermediateFormValues>({
    mode: "onBlur",
    resolver: yupResolver(transferSchema),
  });

  const resetForm = async () => {
    setIntermediateFormValues({
      receiver: "",
      token: "",
      tokenAmount: "",
      tokenSymbol: "",
    });
    setPreflightDetails({
      receiver: "",
      token: "",
      tokenAmount: 0,
      tokenSymbol: "",
    });
    reset({ token: "" });
  };
  return (
    <>
      <PageHeading title="Token Bridge" />
      {shadowForkMessagesEnabled && (
        <Box mb="16px" pl="8px" color="palmNetwork.100" bg="palmNetwork.800">
          <Box pb="32px" pt="48px" maxW="728px" pl="16px">
            <Heading fontSize="24px" mb="16px">
              Shadow Fork Testnet testing
            </Heading>
            <Text mb="16px">
              This application has been updated so that the Palm-Goerli testnet
              bridge now interacts with the temporary &apos;shadow fork&apos;
              Palm Testnet. Bridging between Goerli and the official Palm
              Testnet is currently suspended whilst this testing is carried out.
            </Text>
            <Text mb="16px">
              To use this bridge when sending tokens from the shadow fork Palm
              Testnet to Goerli, you will need to update your wallet to use the
              shadow fork Palm Testnet RPC - this can be added from the
              Configure page.
            </Text>
            <Text mb="16px">
              Please refer to the Palm network documentation for details at{" "}
              <Link>https://docs.palm.io/network-update</Link>
            </Text>
          </Box>
        </Box>
      )}

      <Box mt="8" mb="12" maxW="730px" ml="18px">
        <Heading fontSize="24px" mr="2">
          Transfer DAI, WETH and other tokens to and from Palm
        </Heading>
        <Text mt="8" mb="8">
          The Palm bridge can be used to transfer DAI and other ERC-20 tokens
          between the Palm network and Ethereum. When assets are transferred
          from Ethereum to Palm, the bridge will top up the destination address
          with a small amount of PALM.
        </Text>
        <Text>
          A fee is required to move assets across the bridge from Palm to
          Ethereum, charged in DAI. This fee covers the cost of gas on Ethereum,
          and also goes towards carbon offset projects to account for the
          emissions associated with the part of this transaction that takes
          place on the Ethereum network.
        </Text>
      </Box>
      <Box
        width="540px"
        bg="white"
        border="1px"
        borderColor="brand.300"
        p="32px"
        ml="18px"
      >
        {!isReady ? (
          <Button
            fullsize="true"
            onClick={() => {
              handleConnect();
            }}
          >
            {!address ? "Connect Wallet" : "Check Wallet"}
          </Button>
        ) : walletConnecting ? (
          <p>
            This app requires access to your wallet, <br />
            please login and authorize access to continue.
          </p>
        ) : (
          <>
            <NetworkDisplay location="Origin Network" name={homeChain?.name} />
            <Button
              width="0px"
              mt="8px"
              mb="10px"
              onClick={() => handleSwitchNetwork()}
              disabled={switchingNetworks}
            >
              ↑↓
            </Button>
            <NetworkDisplay
              location="Destination network"
              name={destinationChain?.name}
            />
          </>
        )}
        <form
          onSubmit={handleSubmit(async (values: IntermediateFormValues) => {
            wallet && (await checkIsReady());
            setPreflightModalOpen(true);
          })}
        >
          <Box as={Stack} spacing="50px" direction={"column"} mt="34px">
            <Box as={Stack} spacing="24px" direction={"row"}>
              <FormControl
                isInvalid={!!errors?.token?.message}
                errortext={errors?.token?.message}
                isRequired
              >
                <FormLabel>Token</FormLabel>
                <TokenSelectInput
                  tokens={tokens}
                  name="token"
                  isDisabled={!destinationChain}
                  label={`Balance: `}
                  value={getValues("token")}
                  sync={(tokenAddress) => {
                    setIntermediateFormValues({
                      ...intermediateFormValues,
                      token: tokenAddress,
                      tokenSymbol: tokens[tokenAddress]?.symbol || "",
                    });
                  }}
                  register={register}
                  setValue={setValue}
                  options={
                    Object.keys(tokens).map((t) => ({
                      value: t,
                      label: (
                        <div>
                          <span>{tokens[t]?.symbol || t}</span>
                        </div>
                      ),
                    })) || []
                  }
                />
                <FormErrorMessage>{errors?.token?.message}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors?.tokenAmount?.message}
                errortext={errors?.tokenAmount?.message}
                isRequired
              >
                <FormLabel>Transfer amount</FormLabel>
                <TokenInput
                  name="tokenAmount"
                  tokenSelectorKey="token"
                  tokens={tokens}
                  isDisabled={
                    !destinationChain ||
                    !preflightDetails.token ||
                    preflightDetails.token === ""
                  }
                  placeholder={"0"}
                  value={intermediateFormValues.tokenAmount}
                  onChange={(value: string) => {
                    setIntermediateFormValues({
                      ...intermediateFormValues,
                      tokenAmount: value,
                    });
                  }}
                  register={register}
                />
                <FormErrorMessage>
                  {errors?.tokenAmount?.message}
                </FormErrorMessage>
              </FormControl>
            </Box>

            <FormControl
              isInvalid={!!errors?.receiver?.message}
              errortext={errors?.receiver?.message}
              isRequired
            >
              <FormLabel>Destination address</FormLabel>
              <AddressInput
                name="receiver"
                isDisabled={!destinationChain}
                placeholder="Please enter the receiving address"
                senderAddress={`${address}`}
                value={intermediateFormValues.receiver}
                onChange={(value: string) => {
                  setIntermediateFormValues({
                    ...intermediateFormValues,
                    receiver: value,
                  });
                }}
                register={register}
                setValue={setValue}
              />
              <FormErrorMessage>{errors?.receiver?.message}</FormErrorMessage>
            </FormControl>
          </Box>
          <Box>
            <CurrentGasFees />
          </Box>
          {homeChainIsPalm ? (
            <Alert
              status="info"
              mt={4}
              mb={2}
              colorScheme={"palmNetwork.300"}
              color={"palmNetwork.100"}
            >
              <TimeIcon mr={2} />
              Minimum transfer time: 1 hour (256 blocks)
            </Alert>
          ) : null}
          <Fees
            relayFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals)
                  )
                : bridgeFee
            }
            relayFeeSymbol={
              homeChainIsPalm ? tollTokenSymbol : homeChain?.nativeTokenSymbol
            }
            showGreenFee={homeChainIsPalm}
            greenFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals)
                  )
                : undefined
            }
            greenFeeSymbol={homeChainIsPalm ? tollTokenSymbol : undefined}
            transferAmount={
              preflightDetails ? preflightDetails.tokenAmount : undefined
            }
            transferAmountSymbol={
              preflightDetails && tokens[preflightDetails.token]
                ? tokens[preflightDetails.token].symbol
                : undefined
            }
          />
          <Flex justifyContent="center">
            <Button
              type="submit"
              isDisabled={
                !!errors.receiver || !!errors.token || !!errors.tokenAmount
              }
            >
              Start transfer
            </Button>
          </Flex>
        </form>
        <NetworkUnsupportedModal
          open={!homeChain && isReady}
          network={network}
          supportedNetworks={chainbridgeConfig.chains.map((bc) => bc.networkId)}
        />
        <PreflightModalTransfer
          open={preflightModalOpen}
          close={() => setPreflightModalOpen(false)}
          receiver={preflightDetails?.receiver || ""}
          sender={address || ""}
          start={() => {
            setPreflightModalOpen(false);
            preflightDetails &&
              deposit({
                amount: preflightDetails.tokenAmount,
                recipient: preflightDetails.receiver,
                tokenAddress: preflightDetails.token,
              });
          }}
          sourceNetwork={homeChain?.name || ""}
          targetNetwork={destinationChain?.name || ""}
          tokenSymbol={preflightDetails?.tokenSymbol || ""}
          value={preflightDetails?.tokenAmount || 0}
        />
        <TransferActiveModal
          open={!!transactionStatus}
          close={() => {
            resetDeposit();
            resetForm();
          }}
        />
        <Divider color="palmNetwork.100" pt="24px" />
        <Box
          as={Stack}
          spacing="22px"
          direction={"column"}
          align="left"
          width="540px"
          mt="36px"
        >
          <Box as={Stack} spacing="8px" direction={"row"} align="center">
            <Text fontSize="xs">Bridge operator: </Text>
            <Image src={CONSENSYS} height="32px" alt="Consensys" />
            <Text fontSize="xs">Carbon offsets: </Text>
            <Image src={PATCH} width="64px" alt="Patch" />
          </Box>
          <Box as={VStack} spacing="1px" direction={"row"} align="left">
            <Text fontSize="xs">
              <Link
                fontSize="xs"
                href={"https://consensys.net/privacy-policy/"}
                isExternal
              >
                Privacy Policy &nbsp;
                <ExternalLinkIcon />
              </Link>{" "}
              |{" "}
              <Link
                fontSize="xs"
                href={"https://consensys.net/terms-of-use/"}
                isExternal
              >
                Terms of Use &nbsp;
                <ExternalLinkIcon />
              </Link>
            </Text>
          </Box>
        </Box>
      </Box>
    </>
  );
};
export default TransferPage;

interface INetworkDisplay {
  location: string;
  name: any;
}

const NetworkDisplay: React.FC<INetworkDisplay> = ({
  location,
  name,
}: INetworkDisplay) => {
  return (
    <>
      <Text fontSize="12px" mb="4px">
        {location}
      </Text>
      <Heading fontSize="20px">{name}</Heading>
    </>
  );
};
